// ClipRadar website — Compare v3: no gimmicks. A competitor picker and one
// clean ledger panel: criterion | ClipRadar (highlighted column) | them.
// The same DOM reflows for mobile (criterion spans, us/them side by side).
import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { CRWIcon, type IconName } from '../components/icons';
import { SectionHead } from '../components/primitives';
import logoMark from '../assets/logo-mark.svg';
import jumperIcon from '../assets/jumper-icon.png';
import '../styles/compare.css';

/* ---------- data ---------- */
type Mark = 'yes' | 'no' | 'part';

// [full text, mark, mobile-short] — the short copy is written for the ~150px
// mobile tile; great compare tables cut the copy, not the columns.
type Cell = [string, Mark, string?];

interface Contact {
  id: string;
  name: string;
  sub: string;
  cells: Cell[];
  note: string;
}

const ROWS: { label: string; icon: IconName }[] = [
  { label: 'Visual + speech + faces, one search', icon: 'search' },
  { label: 'Footage stays on your machine', icon: 'lock' },
  { label: 'Pricing', icon: 'tag' },
  { label: 'Lives inside Premiere', icon: 'monitor' },
  { label: 'Life outside the NLE', icon: 'layers' },
  { label: 'Speech search languages', icon: 'globe' },
];

const US = ['All three, fused by Ultra Search', 'Always — zero upload', '$29/mo — or own it for $199', 'Native docked panel', 'Full desktop app included', '110+ languages'];
const US_M = ['All three, fused', 'Zero upload', '$29/mo · own $199', 'Native panel', 'Full desktop app', '110+ languages'];

const CONTACTS: Contact[] = [
  {
    id: 'jumper', name: 'Jumper', sub: 'AI footage search',
    cells: [
      ['People search locked to Pro', 'part', 'People need Pro'],
      ['Local too', 'yes'],
      ['$169 — faces need Pro: $599 or $49/mo', 'part', '$169 · Pro $599'],
      ['Panels for four NLEs', 'yes', '4 NLE panels'],
      ['Standalone app', 'yes'],
      ['Multilingual — 111 languages', 'yes', '111 languages'],
    ],
    note: 'The closest rival — and a good tool. Our wedge: every ClipRadar plan unlocks everything, faces and Ultra Search included — we split billing, not features.',
  },
  {
    id: 'adobe', name: 'Adobe', sub: 'Media Intelligence',
    cells: [
      ['Visual & transcript — no people', 'part', 'No people search'],
      ['Local too', 'yes'],
      ['Tied to the $22.99/mo sub', 'no', 'Needs the sub'],
      ['Built in — index locked to Premiere', 'yes', 'Built in, locked'],
      ['None — Premiere only', 'no', 'Premiere only'],
      ['Visual search is English-only', 'no', 'English-only'],
    ],
    note: 'Adobe proved on-device search matters. We add people search, 110+ languages, and an index you own outside Premiere.',
  },
  {
    id: 'byhand', name: 'By hand', sub: 'Manual scrubbing',
    cells: [
      ['Your memory', 'no'],
      ['Yes — nothing to upload', 'yes', 'Nothing uploads'],
      ['Your weekends', 'no'],
      ['—', 'no'],
      ['Stacks of Explorer windows', 'no', 'Explorer windows'],
      ['—', 'no'],
    ],
    note: 'Still the reigning workflow in most edit bays. Free, familiar — and the most expensive option here: it bills by the weekend.',
  },
  {
    id: 'twelvelabs', name: 'TwelveLabs', sub: 'Video AI API',
    cells: [
      ['Strong multimodal — via API', 'yes', 'Strong — via API'],
      ['Cloud — upload required', 'no', 'Cloud upload'],
      ['Usage-based, per minute indexed', 'no', 'Per-minute billing'],
      ['No NLE integration', 'no', 'No NLE'],
      ['Rodeo app — cloud, early access', 'part', 'Rodeo — cloud'],
      ['Multilingual', 'yes'],
    ],
    note: 'World-class research — and with Rodeo, now an editor-facing app too. Everything still runs in their cloud; ClipRadar is the local instrument inside your NLE.',
  },
  {
    id: 'quickture', name: 'Quickture', sub: 'AI edit assistant',
    cells: [
      ['Transcripts, visuals & faces', 'yes', 'All three'],
      ['Cloud — proxies upload for analysis', 'no', 'Cloud upload'],
      ['$499/mo per seat', 'no', '$499/mo seat'],
      ['Panel for Premiere & Avid', 'yes', 'Premiere & Avid'],
      ['Web dashboard', 'part'],
      ['Not published', 'part'],
    ],
    note: 'Powerful cloud auto-assembly — and a non-starter for NDA footage. ClipRadar never uploads a frame.',
  },
];

/* ---------- pieces ---------- */
function MarkIcon({ m, us }: { m: Mark; us?: boolean }) {
  const cls = us ? 'crwc-m crwc-m-us' : m === 'yes' ? 'crwc-m crwc-m-yes' : m === 'part' ? 'crwc-m crwc-m-part' : 'crwc-m crwc-m-no';
  const d = m === 'yes' ? 'check' : m === 'part' ? 'minus' : 'x';
  return <span className={cls}><CRWIcon d={us ? 'check' : d} size={11} sw={2.6} /></span>;
}

// Competitor monograms — Adobe's real mark, styled letter tiles for the rest.
function ContactMark({ id, size = 18 }: { id: string; size?: number }) {
  if (id === 'adobe') return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FA0F00" d="M13.966 3.027H24v17.946zM10.045 3.027H0v17.946zM12.01 9.64 18.4 20.973h-4.195l-1.911-4.833h-4.68z" />
    </svg>
  );
  if (id === 'byhand') return <span style={{ color: 'var(--fg-2)', display: 'inline-flex' }}><CRWIcon d="user" size={size * 0.85} /></span>;
  // real product icon, like Adobe's mark above (founder, 2026-07-17)
  if (id === 'jumper') return <img src={jumperIcon} width={size} height={size} alt="" style={{ display: 'block' }} />;
  // remaining competitor marks in neutral gray — brand hues here read as accent
  // soup next to the one warm ClipRadar column (A1 law); the letter identifies them
  const map: Record<string, [string, string]> = { twelvelabs: ['12', '#A6AAB2'], quickture: ['Q', '#A6AAB2'] };
  const [glyph, color] = map[id] ?? ['?', '#fff'];
  return <b style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: Math.round(size * (glyph.length > 1 ? 0.62 : 0.85)), letterSpacing: '-0.04em', color, lineHeight: 1 }}>{glyph}</b>;
}

/* ---------- section ---------- */
export function Compare() {
  const [sel, setSel] = useState('jumper');
  const boxRef = useRef<HTMLDivElement | null>(null);
  const prevSel = useRef(sel);
  const target = CONTACTS.find((c) => c.id === sel)!;

  // switch: outgoing text fades, state swaps, incoming text eases in (below)
  const pick = (id: string) => {
    if (id === sel) return;
    const els = boxRef.current?.querySelectorAll('[data-swap]');
    if (!els?.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setSel(id); return; }
    gsap.to(els, { opacity: 0, y: 4, duration: 0.14, ease: 'power2.in', overwrite: true, onComplete: () => setSel(id) });
  };
  useLayoutEffect(() => {
    if (prevSel.current === sel) return;
    prevSel.current = sel;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = boxRef.current?.querySelectorAll('[data-swap]');
    if (els?.length) gsap.fromTo(els, { opacity: 0, y: 4 }, { opacity: 1, y: 0, duration: 0.3, stagger: 0.03, ease: 'power2.out', overwrite: true });
  }, [sel]);

  return (
    <section id="compare" style={{ padding: '90px 24px', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '8%', transform: 'translateX(-50%)', width: 1000, height: 640, background: 'radial-gradient(ellipse, rgba(255,255,255,0.03), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 920, margin: '0 auto', position: 'relative' }}>
        <SectionHead center label="ClipRadar vs alternatives" title="How we stack up."
          sub="Four competitors and your bare hands — pick one, get straight answers." />

        {/* competitor picker */}
        <div className="crwc-pick" role="tablist" aria-label="Pick a competitor">
          {CONTACTS.map((c) => (
            <button key={c.id} className={`crwc-tab${sel === c.id ? ' crwc-tab-on' : ''}`} role="tab"
              aria-selected={sel === c.id} title={`${c.name} — ${c.sub}`} onClick={() => pick(c.id)}>
              <span className="crwc-tab-mark"><ContactMark id={c.id} size={15} /></span>
              {c.name}
            </button>
          ))}
        </div>

        {/* the ledger */}
        <div className="crwc-panel" ref={boxRef}>
          <div className="crwc-hrow">
            <div className="crwc-hlabel">Head to head</div>
            <div className="crwc-cell crwc-us crwc-hus">
              <img src={logoMark} width="14" height="14" alt="" style={{ opacity: 0.95 }} />
              <span>ClipRadar</span>
            </div>
            <div className="crwc-cell crwc-hthem" data-swap>
              <ContactMark id={sel} size={15} />
              <span className="crwc-hthem-name">{target.name}</span>
              <span className="crwc-hthem-sub">{target.sub}</span>
            </div>
          </div>
          {ROWS.map((row, i) => (
            <div className={`crwc-r${i === ROWS.length - 1 ? ' crwc-r-last' : ''}`} key={row.label}>
              <div className="crwc-crit">
                <span className="crwc-crit-icon"><CRWIcon d={row.icon} size={13} /></span>
                {row.label}
              </div>
              <div className="crwc-cell crwc-us">
                <MarkIcon m="yes" us />
                <span className="crwc-txt">
                  <span className="crwc-t-full">{US[i]}</span>
                  <span className="crwc-t-short" aria-hidden="true">{US_M[i]}</span>
                </span>
              </div>
              <div className="crwc-cell crwc-them" data-swap>
                <MarkIcon m={target.cells[i][1]} />
                <span className="crwc-txt">
                  <span className="crwc-t-full">{target.cells[i][0]}</span>
                  <span className="crwc-t-short" aria-hidden="true">{target.cells[i][2] ?? target.cells[i][0]}</span>
                </span>
              </div>
            </div>
          ))}
          <div className="crwc-note" data-swap>
            <span className="crwc-note-label">Field note — {target.name}</span>
            {target.note}
          </div>
        </div>

        <p className="crwc-foot">Based on public docs &amp; pricing, July 2026 — check current feature sets.</p>
      </div>
    </section>
  );
}
